import { useParams } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { isPremiumApp } from "@shared/tiers";
import { usePaidSuite } from "@/components/EventSuiteAccess";
import { remainingLabel } from "@/lib/exportGate";
import { isToolSessionCounted, usageScope, useUsageSnapshot } from "@/lib/usageGate";

/**
 * Says which step spends the trial use, so a counter that stays put after Apply does not look stuck.
 * Loading a file and running the tool take no use; the Download (or other way the result leaves) takes 1.
 * Shown only on a trial session with uses left and nothing counted yet for this tool visit; paid, admin,
 * unknown status and an already-counted visit render nothing.
 */
export function DownloadTrialHint({ toolId: toolIdProp, className = "", inline = false, prefix = "" }: { toolId?: string; className?: string; inline?: boolean; prefix?: string }) {
  const { isAuthenticated } = useAuth();
  const params = useParams<{ id?: string }>();
  const paid = usePaidSuite();
  useUsageSnapshot(); // re-render when the trial/paid state becomes known or changes
  const toolId = toolIdProp ?? params.id ?? "unknown";
  if (toolId === "unknown" || paid || isToolSessionCounted(toolId)) return null;
  const scope = usageScope(isAuthenticated, isAuthenticated ? undefined : getOrCreateDeviceId(), isPremiumApp(toolId));
  const label = remainingLabel(scope);
  if (!label || label.startsWith("0 ")) return null;
  // Inline form sits inside a button subtitle, which already says "Download", so it reads "uses 1 trial use".
  if (inline) return <span data-testid="download-trial-hint">{prefix}uses 1 trial use</span>;
  return (
    <p className={`text-xs text-muted-foreground ${className}`} data-testid="download-trial-hint">
      Download uses 1 trial use
    </p>
  );
}
